import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { ChevronDown, ChevronRight } from "lucide-react";
import { JourneyService } from "@services/journey/journeyService";
import { JourneySummary } from "@services/journey/commands/cmdListJourneys";
import { CopyJourneyStepsPayload } from "@services/journey/commands/cmdCopyJourneySteps";
import ConfirmActionModal from "../contentCatalog/components/ConfirmActionModal";
import CopyFromModal, { CopyLevel } from "./CopyFromModal";
import StepDimensionsModal from "./StepDimensionsModal";

interface CopyState {
  level: CopyLevel;
  targetParentId: number | null;
  targetLabel?: string;
}

interface StepDimension {
  id: number;
  code: string;
  name: string;
  sequence: number;
}

interface StepNode {
  id: number;
  name: string;
  parent_id: number | null;
  sequence: number;
  children?: StepNode[];
  // The steps tree carries each step's billing cells so the row can show them without a
  // request per step. Absent on older payloads, hence optional.
  dimensions?: StepDimension[];
}

const INDENT: Record<number, number> = { 0: 12, 1: 46, 2: 84 };
// Most steps carry one cell per term, so four covers the real cases; beyond that the row
// says how many more rather than letting the window width decide what gets seen.
const DIM_CHIP_LIMIT = 4;
const service = new JourneyService();

function sortTree(nodes: StepNode[]): StepNode[] {
  return [...nodes]
    .sort((a, b) => a.sequence - b.sequence || a.name.localeCompare(b.name))
    .map((n) => ({ ...n, children: n.children ? sortTree(n.children) : [] }));
}

function countDescendants(nodes: StepNode[]): number {
  return nodes.reduce((sum, n) => sum + 1 + countDescendants(n.children ?? []), 0);
}

// Ancestor ids (root → parent, excluding the node itself), or null if not found.
function findAncestors(nodes: StepNode[], id: number, acc: number[] = []): number[] | null {
  for (const n of nodes) {
    if (n.id === id) return acc;
    const hit = findAncestors(n.children ?? [], id, [...acc, n.id]);
    if (hit) return hit;
  }
  return null;
}

interface Props {
  journey: JourneySummary;
  target?: { stepId: number; nonce: number } | null;
}

const JourneyTreeEditor: FC<Props> = ({ journey, target }) => {
  const [tree, setTree] = useState<StepNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [menuId, setMenuId] = useState<number | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<StepNode | null>(null);
  // The (step, dimension) cells pricing addresses. Edited in their own modal rather than inline:
  // dimensions apply to a handful of steps, and this tree already carries 50+ across three levels.
  const [dimensionsFor, setDimensionsFor] = useState<StepNode | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [copyState, setCopyState] = useState<CopyState | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const handledNonce = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await service.getJourneySteps(journey.id);
      setTree(sortTree(data ?? []));
    } catch {
      setError("Failed to load journey steps.");
    } finally {
      setLoading(false);
    }
  }, [journey.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (editingId != null) inputRef.current?.focus();
  }, [editingId]);

  useEffect(() => {
    if (menuId == null) return;
    const close = () => setMenuId(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuId]);

  // Scroll to and briefly highlight a step chosen from the cross-journey search.
  // Runs once per nonce; waits for the tree to load and only acts if the step is here.
  useEffect(() => {
    if (!target || tree.length === 0 || handledNonce.current === target.nonce) return;
    const ancestors = findAncestors(tree, target.stepId);
    if (ancestors === null) return;
    handledNonce.current = target.nonce;
    setCollapsed((prev) => {
      const next = new Set(prev);
      ancestors.forEach((id) => next.delete(id));
      return next;
    });
    setHighlightId(target.stepId);
    const scroll = setTimeout(() => {
      rowRefs.current.get(target.stepId)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    const clear = setTimeout(() => setHighlightId(null), 2200);
    return () => {
      clearTimeout(scroll);
      clearTimeout(clear);
    };
  }, [target, tree]);

  const phaseCount = tree.length;
  const stepCount = useMemo(
    () => tree.reduce((s, p) => s + countDescendants(p.children ?? []), 0),
    [tree],
  );

  const childrenOf = (parentId: number | null): StepNode[] => {
    if (parentId === null) return tree;
    const find = (nodes: StepNode[]): StepNode | null => {
      for (const n of nodes) {
        if (n.id === parentId) return n;
        const hit = find(n.children ?? []);
        if (hit) return hit;
      }
      return null;
    };
    return find(tree)?.children ?? [];
  };

  const findNode = (id: number): StepNode | null => {
    const search = (nodes: StepNode[]): StepNode | null => {
      for (const n of nodes) {
        if (n.id === id) return n;
        const hit = search(n.children ?? []);
        if (hit) return hit;
      }
      return null;
    };
    return search(tree);
  };

  // Drag-to-reorder, within the same group (siblings). Renumbers sequences to the
  // dropped order and persists. Cross-group moves use the ⋮ menu.
  const reorderByDrop = async (target: StepNode) => {
    const id = dragId;
    setDragId(null);
    setDragOverId(null);
    if (id == null || id === target.id) return;
    const dragged = findNode(id);
    if (!dragged || dragged.parent_id !== target.parent_id) return;
    const sibs = childrenOf(target.parent_id).filter((s) => s.id !== id);
    const ti = sibs.findIndex((s) => s.id === target.id);
    sibs.splice(ti < 0 ? sibs.length : ti, 0, dragged);
    try {
      await Promise.all(
        sibs.map((s, i) => (s.sequence !== i ? service.updateStep(journey.id, s.id, { sequence: i }) : null)),
      );
      await load();
    } catch {
      setError("Failed to reorder.");
    }
  };

  const toggleCollapse = (id: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const startEdit = (node: StepNode) => {
    setMenuId(null);
    setDraft(node.name);
    setEditingId(node.id);
  };

  const commitEdit = async () => {
    const id = editingId;
    setEditingId(null);
    if (id == null) return;
    const name = draft.trim();
    if (!name) return;
    try {
      await service.updateStep(journey.id, id, { name });
      await load();
    } catch {
      setError("Failed to rename step.");
    }
  };

  const addChild = async (parentId: number | null, defaultName: string) => {
    setMenuId(null);
    try {
      const created = await service.createStep(journey.id, {
        name: defaultName,
        parent_id: parentId,
        is_supplemental: false, // manager edits the master tree, not one-off custom steps
      });
      if (parentId != null) setCollapsed((p) => { const n = new Set(p); n.delete(parentId); return n; });
      await load();
      if (created?.id) startEdit(created);
    } catch {
      setError("Failed to add step.");
    }
  };

  const remove = (node: StepNode) => {
    setMenuId(null);
    setPendingDelete(node);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await service.deleteStep(journey.id, pendingDelete.id);
      setPendingDelete(null);
      await load();
    } catch (e: any) {
      setError(e?.message || "Couldn't delete this step (it may be in use or have child steps).");
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const move = async (node: StepNode, dir: "up" | "down") => {
    setMenuId(null);
    const siblings = childrenOf(node.parent_id);
    const idx = siblings.findIndex((s) => s.id === node.id);
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= siblings.length) return;
    const order = [...siblings];
    [order[idx], order[target]] = [order[target], order[idx]];
    try {
      // Normalise sequences to the new positions (only PATCH the ones that moved).
      await Promise.all(
        order.map((s, i) => (s.sequence !== i ? service.updateStep(journey.id, s.id, { sequence: i }) : null)),
      );
      await load();
    } catch {
      setError("Failed to reorder.");
    }
  };

  const copySteps = async (payload: CopyJourneyStepsPayload) => {
    await service.copyJourneySteps(journey.id, payload);
    await load();
  };

  const renderRow = (node: StepNode, depth: number) => {
    const kind = depth === 0 ? "phase" : depth === 1 ? "step" : "substep";
    const kids = node.children ?? [];
    const hasKids = kids.length > 0;
    const isCollapsed = collapsed.has(node.id);
    const siblings = childrenOf(node.parent_id);
    const idx = siblings.findIndex((s) => s.id === node.id);

    const rows = [
      <div
        key={node.id}
        ref={(el) => {
          if (el) rowRefs.current.set(node.id, el);
          else rowRefs.current.delete(node.id);
        }}
        className={`jsm-row is-${kind}${dragOverId === node.id ? " drag-over" : ""}${highlightId === node.id ? " is-hit" : ""}`}
        style={{ paddingLeft: INDENT[Math.min(depth, 2)] ?? 84 }}
        draggable={editingId !== node.id}
        onDragStart={(e) => {
          setDragId(node.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragEnd={() => {
          setDragId(null);
          setDragOverId(null);
        }}
        onDragOver={(e) => {
          const d = dragId != null ? findNode(dragId) : null;
          if (d && d.id !== node.id && d.parent_id === node.parent_id) {
            e.preventDefault();
            if (dragOverId !== node.id) setDragOverId(node.id);
          }
        }}
        onDragLeave={() => {
          if (dragOverId === node.id) setDragOverId(null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          reorderByDrop(node);
        }}
      >
        <span className="jsm-handle" title="Drag to reorder within this group">⠿</span>
        {depth < 2 ? (
          <button
            className="jsm-row-caret"
            onClick={() => toggleCollapse(node.id)}
            style={{ visibility: hasKids ? "visible" : "hidden" }}
          >
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </button>
        ) : null}
        {depth > 0 && <span className="jsm-bullet">•</span>}

        {editingId === node.id ? (
          <input
            ref={inputRef}
            className="jsm-name-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") setEditingId(null);
            }}
          />
        ) : (
          <span className="jsm-name" onDoubleClick={() => startEdit(node)} title="Double-click to rename">
            {node.name}
          </span>
        )}

        {/* The step's dimensions, inline. Ops asked to see them without opening the step, and
            they double as the affordance: clicking opens the same modal the row menu does.
            Capped rather than left to clip on a narrow window — a silently cut list reads as
            the whole list, which is worse than saying how many are hidden. */}
        {!!node.dimensions?.length && (
          <button
            className="jsm-dims"
            title="Edit dimensions"
            onClick={() => setDimensionsFor(node)}
          >
            {node.dimensions.slice(0, DIM_CHIP_LIMIT).map((d) => (
              <span key={d.id} className="jsm-dim" title={d.name || d.code}>{d.code}</span>
            ))}
            {node.dimensions.length > DIM_CHIP_LIMIT && (
              <span className="jsm-dim jsm-dim-more">
                +{node.dimensions.length - DIM_CHIP_LIMIT}
              </span>
            )}
          </button>
        )}

        {depth === 0 && <span className="jsm-count">· {kids.length} {kids.length === 1 ? "step" : "steps"}</span>}

        <span className="jsm-spacer" />

        {/* Right-aligned, per the mock on the ticket, but only persistent where it is earned.
            A step that HAS cells keeps its link visible: those rows are what teach the feature
            exists. A step that has none reveals it on hover — dimensions belong on a handful of
            steps, so advertising "Add" on the other ~48 invites work that should not happen. */}
        <button
          className={`jsm-dims-link${node.dimensions?.length ? "" : " jsm-dims-link-quiet"}`}
          onClick={() => setDimensionsFor(node)}
        >
          {node.dimensions?.length ? "Edit dimensions" : "Add dimensions"}
        </button>

        {depth === 0 && (
          <button className="jsm-add-inline" title="Add step" onClick={() => addChild(node.id, "New step")}>
            ＋
          </button>
        )}
        <button className="jsm-kebab" onClick={() => setMenuId(menuId === node.id ? null : node.id)}>
          ⋮
        </button>

        {menuId === node.id && (
          <div className="jsm-menu" onMouseDown={(e) => e.stopPropagation()}>
            <button onClick={() => startEdit(node)}>Rename</button>
            <button disabled={idx <= 0} onClick={() => move(node, "up")}>Move up</button>
            <button disabled={idx >= siblings.length - 1} onClick={() => move(node, "down")}>Move down</button>
            {depth < 2 && (
              <button onClick={() => addChild(node.id, depth === 0 ? "New step" : "New sub-step")}>
                {depth === 0 ? "Add step" : "Add sub-step"}
              </button>
            )}
            {depth === 0 && (
              <button
                onClick={() => {
                  setMenuId(null);
                  setCopyState({ level: "step", targetParentId: node.id, targetLabel: node.name });
                }}
              >
                Copy steps from…
              </button>
            )}
            <button
              onClick={() => {
                setMenuId(null);
                setDimensionsFor(node);
              }}
            >
              Dimensions…
            </button>
            <button className="danger" onClick={() => remove(node)}>Delete</button>
          </div>
        )}
      </div>,
    ];

    if (depth === 0 && !isCollapsed && !hasKids) {
      rows.push(
        <div key={`${node.id}-empty`} className="jsm-empty" style={{ paddingLeft: 46 }}>
          <span className="muted">No steps yet —</span>
          <button className="jsm-link" onClick={() => addChild(node.id, "New step")}>＋ Add step</button>
          <span className="muted">or</span>
          <button
            className="jsm-link"
            onClick={() => setCopyState({ level: "step", targetParentId: node.id, targetLabel: node.name })}
          >
            Copy from…
          </button>
        </div>,
      );
    }

    if (!isCollapsed && hasKids) {
      kids.forEach((c) => rows.push(...renderRow(c, depth + 1)));
    }
    return rows;
  };

  return (
    <div className="jsm-editor">
      <div className="jsm-editor-header">
        <div>
          <div className="jsm-breadcrumb">
            {journey.industry || "—"} › {journey.is_master ? "Master journey" : "Journey"}
          </div>
          <div className="jsm-title">{journey.name}</div>
          <div className="jsm-stats">
            {phaseCount} {phaseCount === 1 ? "phase" : "phases"} · {stepCount}{" "}
            {stepCount === 1 ? "step" : "steps"} · ordered by sequence
          </div>
        </div>
        <div className="jsm-actions">
          <button className="jsm-btn" onClick={() => setCollapsed(new Set())}>Expand all</button>
          <button className="jsm-btn" onClick={() => setCopyState({ level: "phase", targetParentId: null })}>
            Copy from…
          </button>
          <button className="jsm-btn jsm-btn-primary" onClick={() => addChild(null, "New phase")}>
            ＋ Add phase
          </button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-0">
          {error}
        </Alert>
      )}

      <div className="jsm-tree">
        <div className="jsm-tree-head">
          <span className="jsm-tree-head-left">Phases · Steps · Substeps</span>
          <span className="jsm-tree-head-right">⠿ drag to reorder · double-click / ⏎ to rename · ⋮ to add / delete</span>
        </div>
        <div className="jsm-tree-rows">
          {loading && tree.length === 0 ? (
            <div className="jsm-empty" style={{ paddingLeft: 16 }}>
              <span className="muted">Loading…</span>
            </div>
          ) : (
            tree.flatMap((p) => renderRow(p, 0))
          )}
          <div className="jsm-add-phase-footer">
            <button className="jsm-link" onClick={() => addChild(null, "New phase")}>＋  Add phase</button>
          </div>
        </div>
      </div>

      {dimensionsFor && (
        <StepDimensionsModal
          journeyId={journey.id}
          stepId={dimensionsFor.id}
          stepName={dimensionsFor.name}
          onClose={() => {
            setDimensionsFor(null);
            // The modal writes each row immediately, so the tree's chips are stale the moment
            // anything changed. Cheaper to always reload than to thread a changed flag back.
            load();
          }}
        />
      )}

      <ConfirmActionModal
        show={pendingDelete !== null}
        title="Delete journey step"
        message={
          pendingDelete
            ? (() => {
                const kids = countDescendants(pendingDelete.children ?? []);
                const tail = kids > 0 ? ` and its ${kids} child ${kids === 1 ? "step" : "steps"}` : "";
                return `Delete "${pendingDelete.name}"${tail}? This can't be undone.`;
              })()
            : ""
        }
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
        isLoading={deleting}
      />

      <CopyFromModal
        show={copyState !== null}
        level={copyState?.level ?? "phase"}
        currentJourneyId={journey.id}
        targetParentId={copyState?.targetParentId ?? null}
        targetLabel={copyState?.targetLabel}
        onClose={() => setCopyState(null)}
        onCopy={copySteps}
      />
    </div>
  );
};

export default JourneyTreeEditor;
